import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { addDays } from "date-fns";
import { CADataPoints } from "../models/flexible/ca-data-points";
import { CBDataPoints } from "../models/flexible/cb-data-points";
import { IRDataPoints } from "../models/flexible/ir-data-points";
import { TypedFlexEntity } from "../models/flexible/typed-flex-entity";
import { Maybe } from "../models/generated/graphql";
import { ReasonsForDiscontinuation, TimeframeRangeType, TimeframeStartingPoint } from "../types/graphql-global-types";
import { getActualWithinRangeEnd, getActualWithinRangeStart } from "./agreement-utils";
import { getContinuousBenefits, getInitialResponse } from "./flex-entity-utils";
import { neo4jDateToDate } from "./neo4j-date-to-date";

export type TimeframeParamsType = {
	timeframeRangeType?: Maybe<TimeframeRangeType>;
	timeframeStart?: Maybe<number>;
	timeframeEnd?: Maybe<number>;
	timeframeStartingPoint?: Maybe<TimeframeStartingPoint>;
};

export class TimeframeEvaluator {
	constructor(private readonly agreementValidFromDate: Date) {}

	getTimeframe(timeframeParams: TimeframeParamsType, entities: Array<TypedFlexEntity<any>>): Maybe<[Maybe<Date>, Maybe<Date>]> {
		const getEventDate = () => this.getEventDate(timeframeParams.timeframeStartingPoint!, entities);
		const startingPoint = entities.find((entity) => entity.type.isStartingPoint)!;

		switch (timeframeParams.timeframeRangeType) {
			case TimeframeRangeType.STARTING_FROM: {
				const afterEventDate = getEventDate();
				return afterEventDate && [addDays(afterEventDate, timeframeParams.timeframeStart || 0), null];
			}
			case TimeframeRangeType.EVER: {
				return [null, null];
			}
			case TimeframeRangeType.UP_TO: {
				const upToEventDate = getEventDate();
				return upToEventDate && [null, upToEventDate];
			}
			case TimeframeRangeType.WITHIN: {
				const withinEventDate = getEventDate();
				if (!withinEventDate) {
					return null;
				}

				return [
					addDays(withinEventDate, getActualWithinRangeStart(timeframeParams.timeframeStart, timeframeParams.timeframeEnd)),
					addDays(withinEventDate, getActualWithinRangeEnd(timeframeParams.timeframeStart, timeframeParams.timeframeEnd)),
				];
			}
			case TimeframeRangeType.WITHIN_THERAPY_TRIAL: {
				if (!(startingPoint as TypedFlexEntity<CADataPoints>).data.therapyTrialStartDate) {
					return null;
				}
				const therapyTrialStartDate = neo4jDateToDate((startingPoint as TypedFlexEntity<CADataPoints>).data.therapyTrialStartDate);
				const therapyTrialEndtDate = neo4jDateToDate((startingPoint as TypedFlexEntity<CADataPoints>).data.therapyTrialEndDate);
				return therapyTrialStartDate && therapyTrialEndtDate && [therapyTrialStartDate, therapyTrialEndtDate];
			}
			case TimeframeRangeType.WITHIN_THE_COVERAGE_PERIOD: {
				if (!(startingPoint as TypedFlexEntity<CADataPoints>).data.coverageStartDate) {
					return null;
				}
				const startCoveragePeriod = neo4jDateToDate((startingPoint as TypedFlexEntity<CADataPoints>).data.coverageStartDate);
				const endCoveragePeriod = (startingPoint as TypedFlexEntity<CADataPoints>).data.coverageEndDate
					? neo4jDateToDate((startingPoint as TypedFlexEntity<CADataPoints>).data.coverageEndDate)
					: null;

				return [startCoveragePeriod, endCoveragePeriod];
			}
			default:
				throw new Error(
					timeframeParams.timeframeRangeType
						? `Unknown timeframe type: ${timeframeParams.timeframeRangeType}`
						: "Timeframe type is not defined"
				);
		}
	}

	getEventDate(timeframeStartingPoint: TimeframeStartingPoint, entities: Array<TypedFlexEntity<any>>): Maybe<Date> {
		const startingPoint = entities.find((entity) => entity.type.isStartingPoint)!;

		switch (timeframeStartingPoint) {
			case TimeframeStartingPoint.AGREEMENT_START_DATE:
				return neo4jDateToDate(this.agreementValidFromDate);

			case TimeframeStartingPoint.COVERAGE_PERIOD_START:
				return neo4jDateToDate((startingPoint as TypedFlexEntity<CADataPoints>).data.coverageStartDate);

			case TimeframeStartingPoint.TREATMENT_START_DATE:
				return (
					neo4jDateToDate(getInitialResponse(entities)?.therapyStartDate) || neo4jDateToDate(startingPoint.firstPrescriptionDate)
				);

			case TimeframeStartingPoint.INTENDED_START_DATE:
				return (
					neo4jDateToDate((startingPoint as TypedFlexEntity<CADataPoints>).data.intendedStartDate) ||
					neo4jDateToDate((startingPoint as TypedFlexEntity<CADataPoints>).data.coverageStartDate) ||
					neo4jDateToDate((startingPoint as TypedFlexEntity<CADataPoints>).data.therapyTrialStartDate)
				);

			case TimeframeStartingPoint.FIRST_ADMINISTRATION_DATE:
			case TimeframeStartingPoint.FIRST_PRESCRIPTION_DATE:
				return neo4jDateToDate(startingPoint.firstPrescriptionDate);

			case TimeframeStartingPoint.PROGRESSION_DATE:
				return (
					this.getIRProgressionDate(getInitialResponse(entities)) ||
					this.getCBProgressionDate(getContinuousBenefits(entities).find((cb) => !cb.patientEnrolledInTreatment) || null)
				);

			case TimeframeStartingPoint.REQUEST_RECEIPT_DATE:
				return (
					neo4jDateToDate(startingPoint.data[AttributeIdentifiers.RequestReceiptDate]) ||
					neo4jDateToDate(startingPoint.data[AttributeIdentifiers.DiagnosisDate])
				);

			default:
				throw new Error(`Unknown timeframe starting point: ${timeframeStartingPoint}`);
		}
	}

	private getIRProgressionDate = (form?: Maybe<IRDataPoints>): Maybe<Date> => {
		return !form?.patientEnrolledInTreatment &&
			(form?.reasonForDiscontinuation as string) === ReasonsForDiscontinuation.PROGRESSIVE_DISEASE
			? neo4jDateToDate(form?.responseAssessmentDate)
			: null;
	};

	private getCBProgressionDate = (form?: Maybe<CBDataPoints>): Maybe<Date> => {
		return !form?.patientEnrolledInTreatment &&
			(form?.reasonForDiscontinuation as string) === ReasonsForDiscontinuation.PROGRESSIVE_DISEASE
			? neo4jDateToDate(form?.benefitAssessmentDate)
			: null;
	};
}
